import { serializeValue } from "@client/lib/values/size";
import { Dimension } from "@client/types";
import {
  getIsEmptyAddress,
  getIsSameAddress,
  mapFromBillingAddress,
  mapFromShippingAddress,
  mapToBillingAddress,
  mapToShippingAddress
} from "@common/lib/address";
import { isNullOrUndefined } from "@common/lib/misc";
import { getProps } from "@common/lib/object";
import { type Checkout } from "../types";

export const CART_IMAGE_SIZE = serializeValue({
  dimension: Dimension.Height,
  dimensionSize: 48,
  isLocked: true,
  height: 48,
  width: 48,
  useAspectRatio: false
});

export const getIsPargoPickup = (shippingRules: Checkout.ShippingRule[], id: string | null): boolean =>
  shippingRules.find((x) => x.id === id)?.app === "PargoPickup";

export const getOrderAfterShippingRuleChange = (
  order: Checkout.Order,
  shippingRules: Checkout.ShippingRule[],
  newShippingRuleId: string
): Checkout.Order => {
  if (getIsPargoPickup(shippingRules, newShippingRuleId)) {
    // shipping is required and the new shipping rule is Pargo Pickup, thus billing is main now.
    // if filled in billing info is empty or same as shipping, copy the rest shipping into billing
    const billingAddress = mapFromBillingAddress(order);
    if (
      getIsEmptyAddress(billingAddress) ||
      getIsSameAddress(
        mapFromShippingAddress(order),
        billingAddress,
        getProps(billingAddress, (x) => !isNullOrUndefined(x) && x !== "")
      )
    ) {
      return {
        ...order,
        ...mapToBillingAddress(mapFromShippingAddress(order)),
        isSameAsShipping: true,
        shippingRuleId: newShippingRuleId,
        pargoPickupPoint: null
      };
    }

    return {
      ...order,
      shippingRuleId: newShippingRuleId,
      pargoPickupPoint: null
    };
  }

  if (getIsPargoPickup(shippingRules, order.shippingRuleId)) {
    // previous shipping rule was Pargo Pickup, thus shipping is main now.
    // if filled in shipping info is empty or same as billing, copy the rest billing into shipping
    const shippingAddress = mapFromShippingAddress(order);
    if (
      getIsEmptyAddress(shippingAddress) ||
      getIsSameAddress(
        shippingAddress,
        mapFromBillingAddress(order),
        getProps(shippingAddress, (x) => !isNullOrUndefined(x) && x !== "")
      )
    ) {
      return {
        ...order,
        ...mapToShippingAddress(mapFromBillingAddress(order)),
        isSameAsShipping: true,
        shippingRuleId: newShippingRuleId,
        pargoPickupPoint: null
      };
    }

    return {
      ...order,
      shippingRuleId: newShippingRuleId,
      pargoPickupPoint: null
    };
  }

  return {
    ...order,
    shippingRuleId: newShippingRuleId,
    pargoPickupPoint: null
  };
};
